// Trang vận khí trong ngày (công khai): tờ lịch + điểm, tín hiệu riêng theo hồ sơ,
// chi tiết tra cứu, lối mở trang La bàn và tiết khí.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { CalendarDays } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { BOIDE_AUTH_CHANGED_EVENT, getBoideToken } from "@/lib/boide-api";
import { loadDailyFortuneMe } from "@/lib/bootstrap";
import { SiteFooter } from "@/components/site-footer";
import { AlmanacHeader, AlmanacLink } from "@/components/van-khi/almanac-layout";
import { IconLaBan, IconVanKhi, IconXemNgay } from "@/components/nav-icons";
import { VanKhiLichNgayPanel } from "@/components/van-khi/van-khi-lich-ngay";

export const Route = createFileRoute("/van-khi")({
  head: () => ({
    meta: [
      { title: "Vận khí hôm nay - Hướng tốt, ngũ hành, giờ hoàng đạo | Bói Đê" },
      {
        name: "description",
        content:
          "Xem vận khí hôm nay theo lá số của bạn: điểm vận khí, hướng xuất hành tốt, la bàn 8 hướng hợp - kỵ theo ngũ hành khí ngày.",
      },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/van-khi" }],
  }),
  component: VanKhiPage,
});

// ─── Trang ───────────────────────────────────────────────────────────────────

function subscribeAuth(onChange: () => void) {
  window.addEventListener(BOIDE_AUTH_CHANGED_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(BOIDE_AUTH_CHANGED_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

function VanKhiPage() {
  // Match the public server render before reading the browser's session.
  const authenticated = useSyncExternalStore(
    subscribeAuth,
    () => !!getBoideToken(),
    () => false,
  );
  const fortune = useQuery({
    queryKey: ["daily-fortune"],
    queryFn: loadDailyFortuneMe,
    enabled: authenticated,
    staleTime: 30 * 60 * 1000,
  });

  const data = fortune.data;

  return (
    <main className="almanac-page min-h-0 flex-1 overflow-y-auto">
      <div className="almanac-container">
        <AlmanacHeader
          title="Vận khí hôm nay"
          description={
            data
              ? `${new Date(`${data.date}T12:00:00+07:00`).toLocaleDateString("vi-VN", { day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Ho_Chi_Minh" })} · ${data.lunar_date}`
              : "Hiểu nhịp ngày, chọn điều thuận lợi cho bạn."
          }
          icon={<IconVanKhi className="size-8" />}
        />

        {/* Thứ tự (cố ý, giống tab Ngày ở /tong-hop/van-khi): tờ lịch + điểm → tín hiệu
            riêng của bạn → lối sang bài luận → chi tiết tra cứu → hướng / la bàn → tiết khí.
            Điểm chỉ hiện MỘT lần (đồng hồ trong tờ lịch) — vòng điểm riêng cũ trùng y số đó. */}
        <div className="mt-5">
          <VanKhiLichNgayPanel part="summary" />
        </div>

        {!authenticated ? (
          <section className="mt-5 almanac-panel p-6">
            <h2 className="font-display text-base font-bold text-foreground">
              Đăng nhập để xem vận khí theo lá số của bạn
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Vận khí được luận từ ngày sinh trong hồ sơ, nên cần đăng nhập mới cá nhân hoá được.
            </p>
            <Link
              to="/login"
              className="mt-4 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground"
            >
              Đăng nhập
            </Link>
          </section>
        ) : fortune.isPending ? (
          <Skeleton className="mt-5 h-[132px] rounded-2xl" />
        ) : fortune.isError || !data ? (
          <p className="mt-5 rounded-2xl bg-card p-5 text-sm text-muted-foreground">
            Chưa luận được vận khí hôm nay. Vui lòng thử lại sau.
          </p>
        ) : (
          <section aria-labelledby="fortune-overview-title" className="mt-5">
            <h2
              id="fortune-overview-title"
              className="font-display text-lg font-bold text-foreground"
            >
              Tín hiệu hôm nay của bạn
            </h2>
            <p className="mb-3 mt-0.5 text-xs text-muted-foreground">
              Ba tín hiệu quan trọng theo lá số của bạn
            </p>
            <div className="fortune-signals">
              {data.cards.map((card) => (
                <article key={card.key} className="fortune-signal">
                  <p className="text-xs leading-5 text-muted-foreground">{card.label}</p>
                  <p className="my-1 text-balance text-base font-semibold leading-6 text-primary">
                    {card.value}
                  </p>
                  <p className="break-words text-xs leading-5 text-muted-foreground">{card.hint}</p>
                </article>
              ))}
            </div>
          </section>
        )}

        <AlmanacLink
          to="/tong-hop/la-ban"
          title="Mở La bàn"
          description="Xem hướng hợp mệnh và hướng tốt trong ngày"
          icon={<IconLaBan className="size-6" />}
          prominent
        />

        <AlmanacLink
          to="/tong-hop/van-khi"
          title="Bài luận của thầy · vận tuần & tháng"
          description="Hôm nay, tuần này, tháng này miễn phí"
          icon={<CalendarDays className="size-5" />}
        />

        <AlmanacLink
          to="/xem-ngay"
          title="Tìm ngày tốt theo việc"
          description="Cưới hỏi, mua xe, khai trương, nhập trạch…"
          icon={<IconXemNgay className="size-6" />}
        />

        <p className="mt-5 px-2 text-center text-[11px] leading-5 text-muted-foreground">
          Nội dung mang tính tham khảo, dựa trên can chi ngày và ngũ hành bản mệnh trong hồ sơ của
          bạn.
        </p>

        <SiteFooter />
      </div>
    </main>
  );
}
